import { useMemo, useState, type ReactNode } from "react";
import type { AdKitKey } from "types/enums";
import type { ClubhouseAdKitDto } from "entities/responses/adkit";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import { getSimulatorsByCurrentFacility } from "modules/simulator/simulatorSelectors";
import {
  useActivateAdKitMutation,
  useCreateAdKitMutation,
  useGetFacilityAdKitsQuery,
  useSyncAdKitMutation,
} from "../../api/endpoints/adkits";
import { buildCampaignCoverage } from "../../campaignCoverage";
import { summarizeCampaign } from "../../campaignSummary";
import { usePlacementCatalog } from "../../placementCatalog";
import { pluralize } from "../../pluralize";
import { toSimulatorForCampaignState } from "../../simulatorCampaignState";
import { ConfirmActionDialog } from "../detail/ConfirmActionDialog";
import { joinNames } from "../detail/missedUpdateCopy";
import { NameStep, type CopySourceRow } from "./NameStep";
import { HolesQuestion } from "./HolesQuestion";
import { GoLiveStep } from "./GoLiveStep";
import { buildSimulatorChoices } from "./goLiveViewModel";
import { createCampaignErrorMessage } from "./createCampaignError";
import { editDraftFromCampaign, planCampaignEdits } from "./campaignEdits";
import { useSaveCampaignEdits } from "./useSaveCampaignEdits";
import { ImagesQuestion } from "./ImagesQuestion";
import { ImagesStep } from "./ImagesStep";
import { PlacementsStep } from "./PlacementsStep";
import { SetupStepBar } from "./SetupStepBar";
import {
  SETUP_STEPS,
  SETUP_STEP_HEADING,
  SETUP_STEP_LABEL,
  buildCreatePayload,
  draftFromCampaign,
  emptyDraft,
  furthestReachableStep,
  isDraftDirty,
  isPlacementReady,
  isStepComplete,
  placementHasImages,
  togglePlacement,
  unreadyPlacements,
  type CampaignDraft,
  type HoleLoadTiming,
  type PlacementSetup,
  type SetupStep,
} from "./campaignDraft";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

function parseStep(value: string | null): SetupStep | null {
  const n = Number(value);
  return SETUP_STEPS.includes(n as SetupStep) ? (n as SetupStep) : null;
}

const EDIT_STEPS: SetupStep[] = [2, 3];

interface NewCampaignPageProps {
  /**
   * CLUB-2657: setup steps 2 and 3 on an existing campaign, opened at
   * `startAt` when given. Needs the placement catalog loaded already.
   */
  edit?: { campaign: ClubhouseAdKitDto; startAt?: AdKitKey };
}

export function NewCampaignPage({ edit }: NewCampaignPageProps = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { placements: catalog } = usePlacementCatalog();
  const [draft, setDraft] = useState<CampaignDraft>(() =>
    edit ? editDraftFromCampaign(edit.campaign, catalog) : emptyDraft(),
  );
  const updateDraft = (patch: Partial<CampaignDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const [placementIndex, setPlacementIndex] = useState(() =>
    edit?.startAt !== undefined
      ? Math.max(0, draft.placements.indexOf(edit.startAt))
      : 0,
  );
  const [cancelRequested, setCancelRequested] = useState(false);
  const [confirmBlank, setConfirmBlank] = useState(false);
  const [confirmDeselect, setConfirmDeselect] = useState<AdKitKey | null>(null);

  const facilityId = useGetCurrentLocalFacilityId();
  const simulators = useSelector(getSimulatorsByCurrentFacility);
  // Same args as the Ad Spaces list, so this reuses its cache entry.
  const { data: campaigns = [] } = useGetFacilityAdKitsQuery(
    { facilityId, includeDeleted: true },
    { skip: !facilityId },
  );

  const copySources: CopySourceRow[] = useMemo(() => {
    const sims = simulators.map(toSimulatorForCampaignState);
    return [...campaigns]
      .sort((a, b) => b.lastModified.toMillis() - a.lastModified.toMillis())
      .map((campaign) => {
        const summary = summarizeCampaign(campaign);
        return {
          id: String(campaign.id),
          name: campaign.name,
          meta: `${pluralize(summary.placementCount, "placement")} · ${pluralize(summary.imageCount, "image")}`,
          state: campaign.deleted
            ? "Archived"
            : buildCampaignCoverage(campaign, sims).isLive
              ? "Live"
              : "Not live",
        };
      });
  }, [campaigns, simulators]);

  const simulatorChoices = useMemo(
    () => buildSimulatorChoices(simulators),
    [simulators],
  );

  const chooseCopy = () => {
    if (draft.source.kind === "copy") return;
    updateDraft({
      source: { kind: "copy", campaignId: null, campaignName: "" },
    });
  };
  const pickCampaign = (id: string) => {
    const campaign = campaigns.find((c) => String(c.id) === id);
    if (!campaign) return;
    setDraft(draftFromCampaign(campaign, catalog));
    setPlacementIndex(0);
  };
  const resetToBlank = () => {
    setDraft((current) => {
      const copiedName =
        current.source.kind === "copy"
          ? `Copy of ${current.source.campaignName}`
          : null;
      return {
        ...emptyDraft(),
        // Keep what the operator typed; drop only the name the copy filled in.
        name: current.name === copiedName ? "" : current.name,
        description: current.description,
      };
    });
    setPlacementIndex(0);
    setConfirmBlank(false);
  };
  const applyToggle = (key: AdKitKey) => {
    setDraft((current) => togglePlacement(current, key, catalog));
    setPlacementIndex(0);
    setConfirmDeselect(null);
  };
  const toggle = (key: AdKitKey) => {
    const removingWithImages =
      draft.placements.includes(key) && placementHasImages(draft.setup[key]);
    if (removingWithImages) setConfirmDeselect(key);
    else applyToggle(key);
  };
  const deselectName =
    catalog.find((p) => p.key === confirmDeselect)?.name ?? "this placement";

  const chooseBlank = () => {
    if (draft.source.kind !== "copy") return;
    if (draft.source.campaignId === null) resetToBlank();
    else setConfirmBlank(true);
  };

  const furthest = furthestReachableStep(draft, catalog);
  // A step the draft can't reach yet (a reload on ?step=3, say) shows the
  // furthest one it can.
  const firstStep = edit ? 2 : 1;
  const lastStep = edit ? 3 : 4;
  const requested = parseStep(searchParams.get("step")) ?? firstStep;
  const step = Math.max(
    firstStep,
    Math.min(requested, furthest, lastStep),
  ) as SetupStep;

  const edits = useMemo(
    () => (edit ? planCampaignEdits(edit.campaign, draft, catalog) : []),
    [edit, draft, catalog],
  );
  const dirty = edit ? edits.length > 0 : isDraftDirty(draft);
  const guard = useUnsavedChangesGuard(dirty);
  const exitTo = edit ? `/ad-spaces/${edit.campaign.id}` : "/ad-spaces";

  const [createAdKit] = useCreateAdKitMutation();
  const [activateAdKit] = useActivateAdKitMutation();
  const [saving, setSaving] = useState<"draft" | "live" | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // One POST with every instance. Once it succeeds the campaign exists, so
  // setup is left even if going live then fails: retrying here would make a
  // second campaign.
  const save = async (goLive: boolean) => {
    if (saving) return;
    setSaving(goLive ? "live" : "draft");
    setSaveError(null);
    let campaignId;
    try {
      const created = await createAdKit({
        facilityId,
        body: buildCreatePayload({ ...draft, timing }, catalog),
      }).unwrap();
      campaignId = created.result.id;
      if (created.errors?.length) {
        toast.error(
          `Saved, but some images weren't added: ${created.errors.map((e) => e.message).join(" ")}`,
        );
      }
    } catch (err) {
      setSaveError(createCampaignErrorMessage(err));
      setSaving(null);
      return;
    }

    if (goLive) {
      try {
        const result = await activateAdKit({
          facilityId,
          adKitId: campaignId,
          simulatorIds: draft.simulatorIds,
          syncFirst: true,
        }).unwrap();
        const failed = result.errors?.length ?? 0;
        if (failed > 0) {
          toast.error(
            `Saved, but it didn't go live on ${pluralize(failed, "simulator")}. Try again from the Simulators tab.`,
          );
        } else {
          toast.success(
            `Live on ${pluralize(draft.simulatorIds.length, "simulator")}.`,
          );
        }
      } catch {
        toast.error(
          "Saved as a draft, but it couldn't go live. Try again from the Simulators tab.",
        );
      }
    } else {
      toast.success("Draft saved.");
    }

    guard.allowNextLeave();
    void navigate(`/ad-spaces/${campaignId}`);
  };

  // Forward moves add a history entry so the browser's Back returns to the
  // previous step; backward moves replace, so Back doesn't bounce forward.
  const goTo = (next: SetupStep) => {
    setSearchParams(
      (prev) => {
        prev.set("step", String(next));
        return prev;
      },
      { replace: next < step },
    );
  };

  const selected = useMemo(() => {
    const byKey = new Map(catalog.map((p) => [p.key, p]));
    return draft.placements.flatMap((key) => {
      const placement = byKey.get(key);
      return placement ? [placement] : [];
    });
  }, [catalog, draft.placements]);
  const currentIndex = Math.max(
    0,
    Math.min(placementIndex, selected.length - 1),
  );
  const current = selected[currentIndex];

  const currentSetup = current ? draft.setup[current.key] : undefined;
  const updateCurrentSetup = (next: PlacementSetup) => {
    if (!current) return;
    setDraft((d) => ({ ...d, setup: { ...d.setup, [current.key]: next } }));
  };

  const timedPlacement = catalog.find((p) => p.hasTransitions);
  const timing: HoleLoadTiming = draft.timing ?? {
    fadeIn: timedPlacement?.defaultFadeIn ?? 0.5,
    display: timedPlacement?.defaultDisplay ?? 4,
    fadeOut: timedPlacement?.defaultFadeOut ?? 0.5,
  };
  const next = selected[currentIndex + 1];
  const previous = selected[currentIndex - 1];

  const selectStep = (target: SetupStep) => {
    // Jumping to Go live with unfinished placements lands on the first one.
    if (target === 4 && !isStepComplete(3, draft, catalog)) {
      const first = unreadyPlacements(draft, catalog)[0];
      setPlacementIndex(first ? Math.max(0, selected.indexOf(first)) : 0);
      goTo(3);
      return;
    }
    goTo(target);
  };

  const saveEdits = useSaveCampaignEdits(facilityId);
  const [syncAdKit] = useSyncAdKitMutation();
  const [confirmLiveSave, setConfirmLiveSave] = useState(false);
  const sims = useMemo(
    () => simulators.map(toSimulatorForCampaignState),
    [simulators],
  );
  const liveIds = edit
    ? buildCampaignCoverage(edit.campaign, sims).liveSimulatorIds
    : [];
  const nameOf = (simId: string) =>
    simulators.find((sim) => sim.id === simId)?.name ?? simId;
  const onlineLiveIds = liveIds.filter(
    (simId) => sims.find((sim) => sim.simulatorId === simId)?.isOnline,
  );
  const offlineLiveIds = liveIds.filter(
    (simId) => !onlineLiveIds.includes(simId),
  );

  // Calls run in order and stop at the first failure. Whatever happens, the
  // campaign page is where the operator lands: it shows what actually saved,
  // and planning a retry from here would be against a stale campaign.
  const saveChanges = async () => {
    if (!edit || saving) return;
    setConfirmLiveSave(false);
    setSaving("draft");
    const campaignId = edit.campaign.id;
    try {
      const { skipped } = await saveEdits(campaignId, edits);
      if (onlineLiveIds.length > 0) {
        await syncAdKit({
          facilityId,
          adKitId: campaignId,
          simulatorIds: onlineLiveIds,
          imageOverwrite: false,
        }).unwrap();
      }
      if (skipped.length > 0) {
        toast.error(
          `Saved, but some changes weren't made: ${skipped.join(" ")}`,
        );
      } else {
        toast.success(
          onlineLiveIds.length > 0
            ? `Changes saved and sent to ${joinNames(onlineLiveIds.map(nameOf))}.`
            : "Changes saved.",
        );
      }
    } catch (err) {
      toast.error(
        `Some changes didn't save: ${createCampaignErrorMessage(err)}`,
      );
    }
    guard.allowNextLeave();
    void navigate(exitTo);
  };
  const requestSave = () =>
    liveIds.length > 0 ? setConfirmLiveSave(true) : void saveChanges();

  let nextLabel = `Next: ${SETUP_STEP_LABEL[(step + 1) as SetupStep] ?? ""}`;
  let onNext = () => goTo((step + 1) as SetupStep);
  let blockMessage: string | null = null;
  let backLabel = "‹ Back";
  let isSave = false;
  let onBack = () => goTo((step - 1) as SetupStep);

  if (step === 1 && !isStepComplete(1, draft, catalog)) {
    blockMessage =
      draft.source.kind === "copy" && draft.source.campaignId === null
        ? "Pick a campaign to copy."
        : draft.name.trim()
          ? "Shorten the name to 100 characters."
          : "Give the campaign a name.";
  }
  if (step === 2 && !isStepComplete(2, draft, catalog)) {
    blockMessage = "Pick at least one placement.";
  }
  if (step === 3 && current) {
    const setup = draft.setup[current.key];
    if (previous) {
      backLabel = `‹ ${previous.name}`;
      onBack = () => setPlacementIndex(currentIndex - 1);
    }
    if (current.isHoleEnabled && setup && setup.holes.length === 0) {
      blockMessage = `Choose at least one hole for ${current.name}.`;
    } else if (next) {
      nextLabel = `Next placement: ${next.name}`;
      onNext = () => setPlacementIndex(currentIndex + 1);
      if (!isPlacementReady(setup, current)) {
        blockMessage =
          setup?.imageMode === "sponsors"
            ? "Give at least one hole a sponsor first."
            : `Add an image to ${current.name} first.`;
      }
    } else {
      nextLabel = edit ? "Save changes" : "Next: Go live";
      if (edit) {
        onNext = requestSave;
        isSave = true;
      }
      const unready = unreadyPlacements(draft, catalog);
      if (unready.length > 0) {
        blockMessage = `${unready.map((p) => p.name).join(", ")} ${unready.length === 1 ? "isn’t" : "aren’t"} finished yet.`;
      } else if (edit && !dirty) {
        blockMessage = "Nothing has changed yet.";
      }
    }
  }

  const discard = () => {
    setCancelRequested(false);
    if (guard.isBlocked) {
      guard.confirmLeave();
      return;
    }
    guard.allowNextLeave();
    void navigate(exitTo);
  };
  const cancel = () => (dirty ? setCancelRequested(true) : discard());

  const body: Record<SetupStep, ReactNode> = {
    1: (
      <NameStep
        draft={draft}
        onChange={updateDraft}
        copySources={copySources}
        onChooseBlank={chooseBlank}
        onChooseCopy={chooseCopy}
        onPickCampaign={pickCampaign}
      />
    ),
    2: (
      <PlacementsStep
        catalog={catalog}
        selected={draft.placements}
        onToggle={toggle}
      />
    ),
    3: (
      <ImagesStep
        draft={draft}
        placements={selected}
        currentIndex={currentIndex}
        onSelectPlacement={setPlacementIndex}
        timing={timing}
        onTimingChange={(next) => updateDraft({ timing: next })}
      >
        {current?.isHoleEnabled && currentSetup && (
          <HolesQuestion setup={currentSetup} onChange={updateCurrentSetup} />
        )}
        {current && currentSetup && (
          <ImagesQuestion
            number={current.isHoleEnabled ? 2 : 1}
            placement={current}
            setup={currentSetup}
            onChange={updateCurrentSetup}
          />
        )}
      </ImagesStep>
    ),
    4: (
      <GoLiveStep
        draft={draft}
        placements={selected}
        simulators={simulatorChoices}
        onSimulatorIdsChange={(simulatorIds) => updateDraft({ simulatorIds })}
        onEditImages={() => {
          setPlacementIndex(0);
          goTo(3);
        }}
      />
    ),
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-6 pb-8 md:px-6">
      <div className="text-center">
        <div className="text-sm font-semibold text-text-muted">
          {edit ? `Editing ${edit.campaign.name}` : "New campaign"}
        </div>
        <h1 className="poppins mt-1.5 text-3xl font-extrabold tracking-tight text-ink">
          {SETUP_STEP_HEADING[step]}
        </h1>
        <SetupStepBar
          step={step}
          furthest={furthest}
          onSelect={selectStep}
          steps={edit ? EDIT_STEPS : undefined}
        />
      </div>

      {(step === 2 || step === 3) &&
        draft.source.kind === "copy" &&
        draft.source.campaignId !== null && (
          <div className="mx-auto mt-5 max-w-205 rounded-xl border border-good/30 bg-good-soft px-3.5 py-2.5 text-center text-sm text-ink">
            Copied from {draft.source.campaignName}. Change anything you like;
            the original isn&rsquo;t affected.
          </div>
        )}

      {body[step]}

      <ConfirmActionDialog
        open={confirmDeselect !== null}
        onOpenChange={(open) => !open && setConfirmDeselect(null)}
        title={`Remove ${deselectName}?`}
        description="Its images and hole choices are cleared from this campaign. The images stay in your library."
        confirmLabel="Remove"
        onConfirm={() =>
          confirmDeselect !== null && applyToggle(confirmDeselect)
        }
        isLoading={false}
      />
      <ConfirmActionDialog
        open={confirmBlank}
        onOpenChange={setConfirmBlank}
        title="Start from a blank campaign?"
        description={`This clears the placements, holes and images copied from ${draft.source.kind === "copy" ? draft.source.campaignName : "the other campaign"}.`}
        confirmLabel="Start blank"
        onConfirm={resetToBlank}
        isLoading={false}
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" className="rounded-lg" onClick={cancel}>
            Cancel
          </Button>
          {(step > firstStep || previous) && (
            <Button variant="outline" className="rounded-lg" onClick={onBack}>
              {backLabel}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {step === 4 && saveError && (
            <p
              role="alert"
              className="w-full text-right text-sm font-semibold text-destructive"
            >
              {saveError}
            </p>
          )}
          {blockMessage && (
            <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              {blockMessage}
            </span>
          )}
          {step === 4 ? (
            <>
              <Button
                variant="outline"
                className="rounded-lg"
                disabled={saving !== null}
                onClick={() => void save(false)}
              >
                {saving === "draft" ? "Saving…" : "Save draft"}
              </Button>
              <Button
                className="rounded-lg"
                disabled={saving !== null || draft.simulatorIds.length === 0}
                title={
                  draft.simulatorIds.length === 0
                    ? "Choose at least one simulator"
                    : undefined
                }
                onClick={() => void save(true)}
              >
                {saving === "live" ? "Going live…" : "Go live"}
              </Button>
            </>
          ) : (
            <Button
              className="rounded-lg"
              disabled={blockMessage !== null || saving !== null}
              onClick={onNext}
            >
              {saving ? "Saving…" : nextLabel}
              {!isSave && " ›"}
            </Button>
          )}
        </div>
      </div>

      <ConfirmActionDialog
        open={confirmLiveSave}
        onOpenChange={setConfirmLiveSave}
        title="Save and update live bays?"
        description={[
          onlineLiveIds.length > 0 &&
            `Saving updates ${joinNames(onlineLiveIds.map(nameOf))} now.`,
          offlineLiveIds.length > 0 &&
            `${joinNames(offlineLiveIds.map(nameOf))} ${offlineLiveIds.length === 1 ? "is" : "are"} offline and will get the update when you push it from the campaign page.`,
        ]
          .filter(Boolean)
          .join(" ")}
        confirmLabel="Save changes"
        onConfirm={() => void saveChanges()}
        isLoading={saving !== null}
      />

      <AlertDialog
        open={cancelRequested || guard.isBlocked}
        onOpenChange={(open) => {
          if (open) return;
          setCancelRequested(false);
          if (guard.isBlocked) guard.cancelLeave();
        }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {edit ? "Discard your changes?" : "Discard this campaign?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {edit
                ? "Nothing has been saved yet. The campaign stays as it was."
                : "Nothing has been saved yet. Leaving now loses what you’ve set up."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction className="rounded-lg" onClick={discard}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
